<template>
  <div class="meetings-calendar-view d-flex flex-column">

    <!-- ── Toolbar: navegación + selector de vista ── -->
    <div class="d-flex align-center justify-space-between mb-3 flex-wrap" style="gap: 10px;">

      <!-- Prev / Mes-Año / Next -->
      <div class="nav-pill d-flex align-center">
        <v-btn icon small class="nav-arrow" @click="prev" :disabled="loading">
          <v-icon size="20" color="#2D4B95">mdi-chevron-left</v-icon>
        </v-btn>
        <span class="nav-title px-2">
          {{ calendarTitle }}
        </span>
        <v-btn icon small class="nav-arrow" @click="next" :disabled="loading">
          <v-icon size="20" color="#2D4B95">mdi-chevron-right</v-icon>
        </v-btn>
        <v-chip x-small color="white" text-color="#2D4B95" class="ml-2 font-weight-bold count-chip">
          {{ meetings.length }} {{ meetings.length === 1 ? 'reunión' : 'reuniones' }}
        </v-chip>
      </div>

      <!-- Vista toggle -->
      <v-btn-toggle v-model="viewMode" mandatory dense class="view-toggle" v-if="!$vuetify.breakpoint.mobile">
        <v-btn value="calendar" small>
          <v-icon small class="mr-1">mdi-calendar-month-outline</v-icon>
          <span class="caption text-capitalize">Calendario</span>
        </v-btn>
        <v-btn value="agenda" small>
          <v-icon small class="mr-1">mdi-format-list-bulleted</v-icon>
          <span class="caption text-capitalize">Agenda</span>
        </v-btn>
      </v-btn-toggle>
    </div>

    <!-- Barra de carga -->
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="#2D4B95"
      height="2"
      class="mb-2 rounded"
    />

    <!-- ─── Vista: Calendario mensual (Vuetify v-calendar) ─── -->
    <v-card v-if="viewMode === 'calendar'" elevation="1" class="calendar-card overflow-hidden">
      <v-calendar
        ref="vcal"
        v-model="currentDate"
        type="month"
        locale="es"
        :weekdays="[1, 2, 3, 4, 5, 6, 0]"
        :events="calendarEvents"
        :event-color="getEventColor"
        event-overlap-mode="stack"
        @click:date="onDateClick"
        @click:event="onEventClick"
        @change="onCalendarChange"
      >
        <!-- Chip / indicador personalizado dentro de cada celda de día -->
        <template v-slot:event="{ event }">
          <div
            v-if="event.isIndicator"
            class="meeting-indicator"
            @click.stop="openDayDrawer(event.dateKey)"
          >
            <span class="meeting-indicator-dot" />
            <span>{{ event.count }} {{ event.count === 1 ? 'reunión' : 'reuniones' }}</span>
            <v-icon size="12" color="#2D4B95" class="ml-auto">mdi-chevron-right</v-icon>
          </div>
          <div
            v-else
            class="meeting-chip-single"
            :title="event.name"
            @click.stop="openMeeting(event._meeting)"
          >
            <v-icon size="10" color="white" class="mr-1 flex-shrink-0">mdi-account-group-outline</v-icon>
            <span class="meeting-chip-text">{{ event.shortName }}</span>
            <span v-if="event.time" class="meeting-chip-time ml-1">{{ event.time }}</span>
          </div>
        </template>
      </v-calendar>
    </v-card>

    <!-- ─── Vista: Agenda ─── -->
    <v-card v-else elevation="1" class="agenda-card">
      <v-divider />

      <v-card-text class="pa-3 agenda-scroll-area">

        <!-- Sin reuniones -->
        <div
          v-if="!loading && meetings.length === 0"
          class="d-flex flex-column align-center justify-center py-10"
        >
          <v-icon color="grey lighten-2" size="48">mdi-calendar-remove-outline</v-icon>
          <p class="body-2 grey--text text--lighten-1 mt-3 mb-0">Sin reuniones este mes</p>
        </div>

        <!-- Grupos por fecha -->
        <div v-else>
          <div v-for="group in meetingsByDate" :key="group.date" class="mb-4">
            <div class="agenda-date-header mb-2">
              <span class="caption font-weight-bold text-uppercase agenda-date-label">
                {{ group.label }}
              </span>
            </div>

            <v-card
              v-for="meeting in group.meetings"
              :key="meeting.id"
              class="meeting-agenda-card mb-2"
              elevation="0"
              outlined
              @click="openMeeting(meeting)"
            >
              <v-card-text class="pa-3">
                <div class="d-flex align-center" style="gap: 10px;">
                  <div class="meeting-time-col flex-shrink-0">
                    <span class="caption font-weight-bold" style="color:#2D4B95;">{{ formatTime(meeting.start) }}</span>
                    <p v-if="meeting.end" class="mb-0" style="font-size: 10px; color:#9CA3AF; line-height:1.2;">{{ formatTime(meeting.end) }}</p>
                  </div>
                  <div class="agenda-separator flex-shrink-0" :style="{ background: statusBgColor(meeting.status) }" />
                  <div class="flex-grow-1" style="min-width: 0;">
                    <p class="mb-0 body-2 font-weight-medium" style="color:#1F2937; line-height:1.3;">
                      <span style="color:#9CA3AF;">#{{ meeting.number }}</span> {{ meeting.title }}
                    </p>
                    <span v-if="meeting.responsible" class="caption" style="color:#6B7280;">
                      {{ meeting.responsible.name }}
                    </span>
                  </div>
                  <v-chip
                    x-small
                    :color="statusColor(meeting.status)"
                    :text-color="statusTextColor(meeting.status)"
                    class="flex-shrink-0 font-weight-medium"
                  >
                    {{ statusLabel(meeting.status) }}
                  </v-chip>
                  <v-btn icon small :loading="isLoadingVisualization(meeting.id)" @click.stop="changeVisualizationStatus(meeting)">
                    <v-icon small :color="seenStatus(meeting) === 0 ? '#F4516C' : '#3EBFA3'">mdi-bell</v-icon>
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- ─── Columna lateral: reuniones del día seleccionado ─── -->
    <v-navigation-drawer
      v-model="showDayDrawer"
      right
      temporary
      fixed
      width="340"
      class="day-drawer"
    >
      <div class="day-drawer-header d-flex align-center justify-space-between pa-4">
        <div>
          <p class="mb-0 caption grey--text text--darken-1">Reuniones de</p>
          <p class="mb-0 subtitle-2 font-weight-bold text-capitalize" style="color:#2D4B95;">
            {{ selectedDayLabel }}
          </p>
        </div>
        <v-btn icon small @click="showDayDrawer = false">
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </div>
      <v-divider />

      <div class="pa-3 day-drawer-body">
        <div
          v-if="selectedDayMeetings.length === 0"
          class="d-flex flex-column align-center justify-center py-10"
        >
          <v-icon color="grey lighten-2" size="40">mdi-calendar-remove-outline</v-icon>
          <p class="body-2 grey--text text--lighten-1 mt-3 mb-0">Sin reuniones este día</p>
        </div>

        <v-card
          v-for="meeting in selectedDayMeetings"
          :key="meeting.id"
          class="meeting-agenda-card mb-2"
          elevation="0"
          outlined
          @click="openMeeting(meeting)"
        >
          <v-card-text class="pa-3">
            <div class="d-flex align-center" style="gap: 10px;">
              <div class="meeting-time-col flex-shrink-0">
                <span class="caption font-weight-bold" style="color:#2D4B95;">{{ formatTime(meeting.start) }}</span>
                <p v-if="meeting.end" class="mb-0" style="font-size: 10px; color:#9CA3AF; line-height:1.2;">{{ formatTime(meeting.end) }}</p>
              </div>
              <div class="agenda-separator flex-shrink-0" :style="{ background: statusBgColor(meeting.status) }" />
              <div class="flex-grow-1" style="min-width: 0;">
                <p class="mb-0 body-2 font-weight-medium" style="color:#1F2937; line-height:1.3;">
                  <span style="color:#9CA3AF;">#{{ meeting.number }}</span> {{ meeting.title }}
                </p>
                <span v-if="meeting.responsible" class="caption" style="color:#6B7280;">
                  {{ meeting.responsible.name }}
                </span>
              </div>
              <v-chip
                x-small
                :color="statusColor(meeting.status)"
                :text-color="statusTextColor(meeting.status)"
                class="flex-shrink-0 font-weight-medium"
              >
                {{ statusLabel(meeting.status) }}
              </v-chip>
              <v-btn icon small :loading="isLoadingVisualization(meeting.id)" @click.stop="changeVisualizationStatus(meeting)">
                <v-icon small :color="seenStatus(meeting) === 0 ? '#F4516C' : '#3EBFA3'">mdi-bell</v-icon>
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </v-navigation-drawer>

  </div>
</template>

<script>
import moment from 'moment'
import 'moment/locale/es'
import EventBus from '@/helpers/event_bus'
import { getMeeting, changeVisualizationStatus } from '@/helpers/api/meetings'

export default {
  name: 'MeetingsCalendarView',
  props: {
    ended: { type: Boolean, default: false }
  },
  data() {
    const now = new Date()
    return {
      viewMode: this.$vuetify.breakpoint.mobile ? 'agenda' : 'calendar',
      currentDate: moment(now).format('YYYY-MM-DD'),
      currentYear: now.getFullYear(),
      currentMonth: now.getMonth() + 1,
      showDayDrawer: false,
      selectedDayDate: null,
    }
  },
  computed: {
		selectedProject() {
			return this.$store.projectsStore.state.selectedProject
		},
    meetings() {
      return (this.$store.meetingStore.state.listadoMeetings &&
              this.$store.meetingStore.state.listadoMeetings.data) || []
    },
    loading() {
      return this.$store.meetingStore.state.listadoMeetings.status === 99
    },
    calendarTitle() {
      return moment(this.currentDate).locale('es').format('MMMM YYYY')
    },
    // Agrupa las reuniones por día y arma UN solo "evento" visual por día:
    // - si hay 1 reunión -> chip normal (abre la reunión)
    // - si hay 2+ reuniones -> chip indicador "N reuniones" (abre la barra lateral)
    calendarEvents() {
      const byDate = {}
      this.meetings.forEach(m => {
        if(!m.start) return
        const key = moment(m.start).format('YYYY-MM-DD')
        if(!byDate[key]) byDate[key] = []
        byDate[key].push(m)
      })

      return Object.keys(byDate).map(dateKey => {
        const dayMeetings = byDate[dateKey]

        if(dayMeetings.length > 1) {
          return {
            name: `${dayMeetings.length} reuniones`,
            start: dateKey,
            end: dateKey,
            timed: false,
            color: '#2D4B95',
            isIndicator: true,
            dateKey,
            count: dayMeetings.length,
          }
        }

        const m = dayMeetings[0]
        const startStr = moment(m.start).format('YYYY-MM-DD HH:mm')
        const endStr = m.end ? moment(m.end).format('YYYY-MM-DD HH:mm') : startStr
        return {
          name: `#${m.number} ${m.title}`,
          shortName: m.title.length > 16 ? m.title.slice(0, 16) + '…' : m.title,
          time: moment(m.start).format('HH:mm'),
          start: startStr,
          end: endStr,
          color: this.statusBgColor(m.status),
          timed: true,
          isIndicator: false,
          dateKey,
          _meeting: m,
        }
      })
    },
    meetingsByDate() {
      const byDate = {}
      this.meetings.forEach(m => {
        const key = moment(m.start).format('YYYY-MM-DD')
        if(!byDate[key]) byDate[key] = []
        byDate[key].push(m)
      })
      return Object.keys(byDate)
        .sort()
        .map(date => ({
          date,
          label: moment(date).locale('es').format('dddd D [de] MMMM'),
          meetings: byDate[date],
        }))
    },
    selectedDayMeetings() {
      if(!this.selectedDayDate) return []
      return this.getDayMeetings(this.selectedDayDate)
        .slice()
        .sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf())
    },
    selectedDayLabel() {
      if(!this.selectedDayDate) return ''
      return moment(this.selectedDayDate).locale('es').format('dddd D [de] MMMM')
    },
  },
  watch: {
    selectedProject: {
			handler() {
				if(!this.selectedProject) return
				this.loadMeetings()
			},
			immediate: true
		}
  },
  mounted() {
		EventBus.$on('reloadMeetings', () => {
			this.loadMeetings()
		})
  },
	beforeDestroy() {
		EventBus.$off('reloadMeetings')
	},
  methods: {
    getProjectId() {
      if(this.$store.projectsStore.state.selectedProject) {
        return this.$store.projectsStore.state.selectedProject.id
      }
      return parseInt(localStorage.getItem('selectedProjectID'))
    },
    loadMeetings() {
      const project_id = this.getProjectId()
      if(!project_id) return
      const base  = moment({ year: this.currentYear, month: this.currentMonth - 1 })
      const start = base.clone().startOf('month').format('YYYY-MM-DD')
      const end   = base.clone().endOf('month').format('YYYY-MM-DD')
      this.$store.meetingStore.dispatch('getMyMeetings', { project_id, start_date: start, end_date: end })
    },

    // ── Navegación ──
    prev() {
      if(this.viewMode === 'calendar' && this.$refs.vcal) {
        this.$refs.vcal.prev()
      } else {
        const d = moment({ year: this.currentYear, month: this.currentMonth - 1 }).subtract(1, 'month')
        this.setCurrentMonth(d)
      }
    },
    next() {
      if(this.viewMode === 'calendar' && this.$refs.vcal) {
        this.$refs.vcal.next()
      } else {
        const d = moment({ year: this.currentYear, month: this.currentMonth - 1 }).add(1, 'month')
        this.setCurrentMonth(d)
      }
    },
    setCurrentMonth(m) {
      this.currentYear  = m.year()
      this.currentMonth = m.month() + 1
      this.currentDate  = m.format('YYYY-MM-DD')
      this.loadMeetings()
    },

    // ── Eventos del calendario Vuetify ──
    onCalendarChange({ start }) {
      if(start.year !== this.currentYear || start.month !== this.currentMonth) {
        this.currentYear  = start.year
        this.currentMonth = start.month
        this.currentDate  = start.date
        this.loadMeetings()
      }
    },
    // Click en el número del día -> siempre abre la barra lateral con las
    // reuniones de ese día (o el mensaje de "sin reuniones" si no hay).
    onDateClick({ date }) {
      this.openDayDrawer(date)
    },
    // Click en el chip de una reunión (evento):
    // - si es el indicador de "N reuniones" -> abre la barra lateral
    // - si es una reunión única -> abre esa reunión directamente
    onEventClick({ event }) {
      if(event.isIndicator) {
        this.openDayDrawer(event.dateKey)
      } else {
        this.openMeeting(event._meeting)
      }
    },

    // ── Helpers de día / columna lateral ──
    getDayMeetings(date) {
      return this.meetings.filter(m => m.start && moment(m.start).format('YYYY-MM-DD') === date)
    },
    openDayDrawer(date) {
      this.selectedDayDate = date
      this.showDayDrawer = true
    },

    // ── Helpers ──
    async openMeeting(meeting) {
      this.$store.meetingStore.commit('setCurrentMeeting', { loading: true })
      const response = await getMeeting({ meeting_id: meeting.id })
      if (response.code === 200) {
        const meetingData = response.meeting
        meetingData.prev_seen_by_me = meeting.seen_by_me || null
        this.$store.meetingStore.commit('setCurrentMeeting', meetingData)
      } else if (response.code === 403) {
        this.$store.meetingStore.commit('setCurrentMeeting', null)
      }
    },
    seenStatus(meeting) {
      return Number(meeting?.seen_by_me?.seen_status ?? meeting?.seen_status ?? 0)
    },
    isLoadingVisualization(meetingId) {
      return this.$store.meetingStore.state.loadingVisualizationChanges.includes(meetingId)
    },
    async changeVisualizationStatus(meeting) {
      const current = this.seenStatus(meeting)
      const status = current === 0 ? 1 : current === 1 ? 2 : 0
      this.$store.meetingStore.commit('addLoadingVisualizationChange', meeting.id)
      try {
        const response = await changeVisualizationStatus({ meeting_id: meeting.id, status })
        if (response.code === 200) {
          this.$set(meeting, 'seen_by_me', { ...(meeting.seen_by_me || {}), seen_status: status })
        }
      } finally {
        this.$store.meetingStore.commit('removeLoadingVisualizationChange', meeting.id)
      }
    },
    getEventColor(event) {
      return event.color || '#2D4B95'
    },
    formatTime(dateStr) {
      return dateStr ? moment(dateStr).format('HH:mm') : ''
    },
    statusLabel(status) {
      const map = { 1: 'Activa', 2: 'En curso', 3: 'Finalizada' }
      return map[status] || 'Activa'
    },
    statusBgColor(status) {
      const map = { 1: '#2D4B95', 2: '#D97706', 3: '#059669' }
      return map[status] || '#2D4B95'
    },
    statusColor(status) {
      const map = { 1: '#EEF2FF', 2: '#FEF3C7', 3: '#ECFDF5' }
      return map[status] || '#EEF2FF'
    },
    statusTextColor(status) {
      const map = { 1: '#2D4B95', 2: '#92400E', 3: '#065F46' }
      return map[status] || '#2D4B95'
    },
  }
}
</script>

<style scoped>
.meetings-calendar-view { width: 100%; flex: 1; }

/* ── Navegación mes/año destacada ── */
.nav-pill {
  background: #EEF2FF;
  border-radius: 10px;
  padding: 4px 6px;
  gap: 2px;
}
.nav-arrow {
  background: white !important;
  box-shadow: 0 1px 2px rgba(45, 75, 149, 0.15) !important;
}
.nav-title {
  color: #2D4B95;
  font-weight: 700;
  font-size: 15px;
  text-transform: capitalize;
  min-width: 130px;
  text-align: center;
}
.count-chip { box-shadow: 0 1px 2px rgba(45, 75, 149, 0.15); }

/* ── Toggle de vistas ── */
.view-toggle {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}
.view-toggle .v-btn { letter-spacing: 0 !important; }
.view-toggle .v-btn--active {
  background-color: #2D4B95 !important;
  color: white !important;
}
.view-toggle .v-btn--active .v-icon {
  color: white !important;
}

/* ── Calendario Vuetify ── */
.calendar-card { border-radius: 8px; flex: 1; }

/* Chip de reunión única dentro de la celda de día */
.meeting-chip-single {
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  overflow: hidden;
  border-radius: 4px;
  line-height: 1;
  padding: 2px 5px;
  cursor: pointer;
  box-sizing: border-box;
}
.meeting-chip-text {
  font-size: 11px;
  color: white;
  font-weight: 500;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}
.meeting-chip-time {
  font-size: 10px;
  color: rgba(255,255,255,0.85);
  white-space: nowrap;
  flex-shrink: 0;
}

/* Chip indicador "N reuniones" */
.meeting-indicator {
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  background: #EEF2FF;
  color: #2D4B95;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  box-sizing: border-box;
  transition: background 0.15s ease;
}
.meeting-indicator:hover { background: #DCE3FA; }
.meeting-indicator-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #2D4B95;
  margin-right: 5px;
  flex-shrink: 0;
}

/* Separación entre el número del día y el chip de evento */
/deep/ .v-calendar-weekly__day-label { margin-bottom: 2px; }
/deep/ .v-calendar-monthly .v-calendar-weekly__head-weekday {
  color: #2D4B95 !important;
  font-weight: 600;
  font-size: 12px;
  background: #FAFBFF;
}
/deep/ .v-calendar-monthly .v-calendar-weekly__day-label .v-btn {
  font-weight: 600;
}
/deep/ .v-calendar-monthly .v-calendar-weekly__day.v-present {
  background: #F5F7FF;
	border-left: #e0e0e0 1px solid;
}
/deep/ .v-calendar-weekly__day {
  min-height: 84px;
  cursor: pointer;
}
/deep/ .v-calendar .v-event {
  border-radius: 4px !important;
  margin: 2px 2px 1px !important;
}

/* ── Agenda ── */
.agenda-card { border-radius: 8px; overflow: hidden; }
.agenda-scroll-area { max-height: 520px; overflow-y: auto; }

.agenda-date-header {
  padding-bottom: 4px;
  border-bottom: 1px solid #E5E7EB;
}
.agenda-date-label {
  color: #2D4B95;
  letter-spacing: 0.07em;
}
.agenda-separator {
  width: 3px;
  height: 36px;
  border-radius: 2px;
}
.meeting-time-col {
  min-width: 40px;
  text-align: center;
}
.meeting-agenda-card {
  border-radius: 8px !important;
  border-color: #E5E7EB !important;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.meeting-agenda-card:hover {
  box-shadow: 0 2px 8px rgba(45, 75, 149, 0.12) !important;
  border-color: #93A8D9 !important;
}

/* ── Columna lateral (drawer) ── */
.day-drawer { z-index: 20; }
.day-drawer-header { background: #FAFBFF; }
.day-drawer-body { overflow-y: auto; }
</style>